import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { updateUserProfile, markUserAsExisting } from '../slices/authSlice';
import { 
  Box, 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Paper, 
  Avatar,
  Divider,
  InputAdornment,
  Snackbar,
  Alert,
  MenuItem,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import EditIcon from '@mui/icons-material/Edit';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import BusinessIcon from '@mui/icons-material/Business';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import WcIcon from '@mui/icons-material/Wc';

// Lista de países para el select
const countries = [
  { code: 'ES', label: 'España' },
  { code: 'MX', label: 'México' },
  { code: 'CO', label: 'Colombia' },
  { code: 'AR', label: 'Argentina' },
  { code: 'PE', label: 'Perú' },
  { code: 'CL', label: 'Chile' },
  { code: 'US', label: 'Estados Unidos' },
  { code: 'CA', label: 'Canadá' },
  { code: 'OT', label: 'Otro' }
];

// Lista de estados civiles
const maritalStatuses = [
  { value: 'single', label_es: 'Soltero/a', label_en: 'Single' },
  { value: 'married', label_es: 'Casado/a', label_en: 'Married' },
  { value: 'divorced', label_es: 'Divorciado/a', label_en: 'Divorced' },
  { value: 'separated', label_es: 'Separado/a', label_en: 'Separated' },
  { value: 'widowed', label_es: 'Viudo/a', label_en: 'Widowed' }
];

const UserProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const user = useSelector(state => state.auth.user);
  
  // Estado para el formulario
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    occupation: '',
    employmentType: 'employee',
    maritalStatus: '',
    hasDependents: 'no',
    dependentsCount: 0,
    bio: '',
    profilePhoto: ''
  });
  
  // Verificar si es un nuevo usuario (sin datos completos)
  const isNewUser = useSelector(state => state.auth.isNewUser) || 
                   (user && (!user.occupation || !user.maritalStatus));
                   
  // Verificar si viene directamente del registro
  const [comingFromRegistration, setComingFromRegistration] = useState(false);
  
  useEffect(() => {
    const isComingFromRegistration = localStorage.getItem('comingFromRegistration') === 'true';
    setComingFromRegistration(isComingFromRegistration);
  }, []);
  
  // Estados adicionales para UI
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  // Estado de edición - determinado por localStorage o si es un nuevo usuario
  const [isEditing, setIsEditing] = useState(false);
  
  // Verificar el modo de vista al iniciar el componente
  useEffect(() => {
    const viewMode = localStorage.getItem('profileViewMode');
    
    // Si viene desde WelcomePage, mostrar en modo lectura
    if (viewMode === 'readonly') {
      setIsEditing(false);
      // Limpiar el localStorage para futuras visitas
      localStorage.removeItem('profileViewMode');
    } else {
      // Para nuevos usuarios, comenzamos automáticamente en modo de edición
      setIsEditing(isNewUser);
    }
  }, [isNewUser]);
  
  // Cargar los datos del usuario cuando el componente se monte
  useEffect(() => {
    if (user) {
      // Recuperar datos de registro si están en localStorage
      const registeredUser = localStorage.getItem('registeredUser') ? 
        JSON.parse(localStorage.getItem('registeredUser')) : null;
        
      const updatedFormData = {
        // Priorizar datos de usuario en redux, luego los del localStorage del registro, y finalmente cadena vacía
        firstName: user.firstName || (registeredUser?.firstName || ''),
        lastName: user.lastName || (registeredUser?.lastName || ''),
        email: user.email || (registeredUser?.email || ''),
        phone: user.phone_number || user.phone || (registeredUser?.phone || ''),
        country: user.country || '',
        city: user.city || '',
        occupation: user.occupation || '',
        employmentType: user.employmentType || 'employee',
        maritalStatus: user.maritalStatus || '',
        // El backend maneja has_dependents como boolean real; el <Select>
        // de este formulario usa 'yes'/'no' como value — se traduce acá.
        hasDependents: user.hasDependents === true ? 'yes' : 'no',
        dependentsCount: parseInt(user.dependentsCount, 10) || 0,
        displayName: user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        bio: user.bio || '',
        profilePhoto: user.profilePhoto || ''
      };
      
      setFormData(updatedFormData);
      
      console.log('Cargando datos de usuario:', { 
        reduxUser: user, 
        registeredUser: registeredUser,
        formData: updatedFormData
      });
      
      // Si hay una foto de perfil en el usuario, mostrarla
      if (user.profilePhoto) {
        console.log('Se encontró foto de perfil:', user.profilePhoto.substring(0, 50) + '...');
      } else {
        console.log('No se encontró foto de perfil para el usuario');
      }
      
      // Si es un nuevo usuario, activar automáticamente el modo edición
      if (isNewUser) {
        setIsEditing(true);
      }
    }
  }, [user, isNewUser]);
  
  // Obtener las iniciales del nombre para el avatar
  const getUserInitials = () => {
    if (formData.firstName && formData.lastName) {
      return `${formData.firstName.charAt(0)}${formData.lastName.charAt(0)}`;
    } else if (formData.firstName) {
      return formData.firstName.charAt(0);
    }
    return 'U';
  };
  
  // Manejar cambios en los campos del formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log('Campo cambiado:', name, value);
    
    // Manejar cambios especiales para ciertos campos
    if (name === 'dependentsCount') {
      // Asegurar que sea un número entero
      const numValue = parseInt(value, 10) || 0;
      setFormData(prev => ({
        ...prev,
        [name]: numValue
      }));
    } else if (name === 'hasDependents') {
      // Si cambia hasDependents a "no", reiniciamos dependentsCount a 0
      // Si cambia a "yes", mantenemos el valor actual o lo inicializamos a 1
      const dependentsCount = value === 'yes' ? (formData.dependentsCount || 1) : 0;
      
      setFormData(prev => ({
        ...prev,
        [name]: value,
        dependentsCount: dependentsCount
      }));
    } else {
      // Comportamiento normal para otros campos
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Manejar la carga de foto de perfil
  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        // El resultado es una URL de datos (data URL) que representa la imagen
        setFormData(prev => ({
          ...prev,
          profilePhoto: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Activar modo edición
  const handleEdit = () => {
    // Cuando se activa el modo edición desde la página de perfil
    // asegurarse de que los datos del usuario estén completos
    setIsEditing(true);
    
    // Registramos que el usuario ha activado el modo edición manualmente
    localStorage.setItem('profileEditActivated', 'true');
  };
  
  // Cancelar edición
  const handleCancel = () => {
    // Restaurar datos originales
    if (user) {
      // Recuperar datos de registro si están en localStorage
      const registeredUser = localStorage.getItem('registeredUser') ? 
        JSON.parse(localStorage.getItem('registeredUser')) : null;
        
      setFormData({
        // Priorizar datos de usuario en redux, luego los del localStorage del registro, y finalmente cadena vacía
        firstName: user.firstName || (registeredUser?.firstName || ''),
        lastName: user.lastName || (registeredUser?.lastName || ''),
        displayName: user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        email: user.email || (registeredUser?.email || ''),
        phone: user.phone_number || user.phone || (registeredUser?.phone || ''),
        country: user.country || '',
        city: user.city || '',
        occupation: user.occupation || '',
        employmentType: user.employmentType || 'employee',
        maritalStatus: user.maritalStatus || '',
        // El backend maneja has_dependents como boolean real; el <Select>
        // de este formulario usa 'yes'/'no' como value — se traduce acá.
        hasDependents: user.hasDependents === true ? 'yes' : 'no',
        dependentsCount: parseInt(user.dependentsCount, 10) || 0,
        bio: user.bio || '',
        profilePhoto: user.profilePhoto || ''
      });
    }
    setIsEditing(false);
  };
  
  // Guardar cambios
  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Validar campos requeridos para usuarios nuevos
    if (isNewUser && (!formData.occupation || !formData.maritalStatus)) {
      setError(language === 'es' ? 'Por favor complete todos los campos requeridos.' : 'Please fill all required fields.');
      setLoading(false);
      return;
    }
    
    // Construir objeto con todos los datos del perfil para la API
    const updatedUser = {
      ...formData,
      displayName: formData.displayName || `${formData.firstName} ${formData.lastName}`.trim(),
      dependentsCount: formData.hasDependents === 'yes' ? formData.dependentsCount : 0,
      isNewUser: false
    };
    
    console.log('Enviando datos del perfil al backend:', updatedUser);
    
    // Actualizar en la base de datos a través del Redux Thunk
    dispatch(updateUserProfile(updatedUser))
      .unwrap()
      .then(response => {
        console.log('Respuesta del servidor:', response);
        
        // Guardar en localStorage después de éxito en el servidor
        localStorage.setItem('user', JSON.stringify(response.user || updatedUser));
        localStorage.setItem('isNewUser', 'false');
        
        // Actualizar UI
        setLoading(false);
        setSuccess(true);
        setIsEditing(false);
        
        // Actualizar estado de Redux
        dispatch(markUserAsExisting());
        
        // Mostrar mensaje de éxito
        console.log('Perfil actualizado correctamente con ID:', response.user?.id || 'desconocido');
        
        // Si viene del registro, limpiar el flag
        if (comingFromRegistration) {
          localStorage.removeItem('comingFromRegistration');

          // Antes esto redirigía a /welcome, una pantalla de onboarding
          // vieja que depende de un endpoint que ya no existe en el
          // backend nuevo. El siguiente paso real del onboarding es
          // Cuentas Bancarias (ver OnboardingSidebar), así que vamos ahí
          // directo.
          setTimeout(() => {
            navigate('/accounts');
          }, 1500);
        }
        // Para actualizaciones normales, quedarse en la página de perfil
      })
      .catch(error => {
        console.error('Error al guardar el perfil:', error);
        setLoading(false);
        setError(language === 'es' ? 'Error al guardar el perfil. Intente nuevamente.' : 'Error saving profile. Please try again.');
      });
  };
  
  // Cerrar notificaciones
  const handleCloseSnackbar = () => {
    setSuccess(false);
    setError(null);
  };
  
  return (
    <Container maxWidth="sm" sx={{ py: 1, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Paper elevation={3} sx={{ p: 1.5, pt: 3, borderRadius: 2, flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar 
                sx={{ 
                  width: 60, 
                  height: 60, 
                  bgcolor: 'primary.main',
                  fontSize: '1.5rem',
                  mr: 2
                }}
                src={formData.profilePhoto}
              >
                {getUserInitials()}
              </Avatar>
              
              {isEditing && (
                <IconButton 
                  size="small" 
                  sx={{ 
                    position: 'absolute', 
                    bottom: -5, 
                    right: 5, 
                    bgcolor: 'background.paper', 
                    boxShadow: 1,
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                  component="label"
                >
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handlePhotoUpload}
                  />
                  <PhotoCameraIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Box>
              <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                {language === 'es' ? 'Perfil de Usuario' : 'User Profile'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {isEditing 
                  ? (language === 'es' ? 'Edite su información personal' : 'Edit your personal information')
                  : (language === 'es' ? 'Información de su perfil' : 'Your profile information')
                }
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
            {language === 'es' ? 'Perfil' : 'Profile'}
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ flexGrow: 1, overflow: 'auto', mb: 2, mt: 5 }}>
          {isEditing ? (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={1.5}>
                {/* Campo de nombre para mostrar */}
                <Grid item xs={12} sx={{ pt: 3 }}>
                  <TextField
                    name="displayName"
                    label={language === 'es' ? 'Nombre para mostrar' : 'Display Name'}
                    value={formData.displayName}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    size="small"
                    required={isNewUser}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                {/* Teléfono / WhatsApp (11 ago 2026): el campo phone_number
                    ya existía en el backend (User.phone_number) y en el
                    payload de este mismo formulario (ver handleSubmit más
                    abajo y updateUserProfile en authSlice.js), pero nunca se
                    agregó el <TextField> — el dato no tenía forma de
                    ingresarse desde la UI. Se agrega ahora porque además de
                    mostrarse en el perfil, este es el número que el backend
                    usa para identificar de qué cuenta es un mensaje de voz
                    entrante por WhatsApp (ver backend_new/app/api/v1/
                    endpoints/whatsapp.py). */}
                <Grid item xs={12}>
                  <TextField
                    name="phone"
                    label={language === 'es' ? 'Teléfono (WhatsApp)' : 'Phone (WhatsApp)'}
                    placeholder="+51987654321"
                    value={formData.phone}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    size="small"
                    helperText={
                      language === 'es'
                        ? 'Con código de país (ej. +51 para Perú). Es el número desde el que puedes registrar gastos por WhatsApp mandando una nota de voz.'
                        : 'Include country code (e.g. +51 for Peru). This is the number you can use to log expenses via WhatsApp voice notes.'
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 0.5 }}>
                    <Typography variant="caption" color="textSecondary">
                      {language === 'es' ? 'Información profesional' : 'Professional information'}
                    </Typography>
                  </Divider>
                </Grid>

                {/* Ocupación */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="occupation"
                    label={language === 'es' ? 'Ocupación' : 'Occupation'}
                    value={formData.occupation}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    required={isNewUser}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <WorkIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                {/* Tipo de empleo */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    name="employmentType"
                    label={language === 'es' ? 'Tipo de empleo' : 'Employment Type'}
                    value={formData.employmentType}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    required={isNewUser}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value="employee">{language === 'es' ? 'Asalariado' : 'Employee'}</MenuItem>
                    <MenuItem value="selfEmployed">{language === 'es' ? 'Independiente' : 'Self-employed'}</MenuItem>
                    <MenuItem value="both">{language === 'es' ? 'Ambos' : 'Both'}</MenuItem>
                  </TextField>
                </Grid>
                
                {/* Estado civil */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    name="maritalStatus"
                    label={language === 'es' ? 'Estado civil' : 'Marital Status'}
                    value={formData.maritalStatus}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    required={isNewUser}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <WcIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value="single">{language === 'es' ? 'Soltero/a' : 'Single'}</MenuItem>
                    <MenuItem value="married">{language === 'es' ? 'Casado/a' : 'Married'}</MenuItem>
                    <MenuItem value="divorced">{language === 'es' ? 'Divorciado/a' : 'Divorced'}</MenuItem>
                    <MenuItem value="widowed">{language === 'es' ? 'Viudo/a' : 'Widowed'}</MenuItem>
                  </TextField>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 0.5 }}>
                    <Typography variant="caption" color="textSecondary">
                      {language === 'es' ? 'Dependientes' : 'Dependents'}
                    </Typography>
                  </Divider>
                </Grid>
                
                {/* Tiene dependientes */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    name="hasDependents"
                    label={language === 'es' ? '¿Tiene dependientes?' : 'Has dependents?'}
                    value={formData.hasDependents}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    required={isNewUser}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FamilyRestroomIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value="yes">{language === 'es' ? 'Sí' : 'Yes'}</MenuItem>
                    <MenuItem value="no">{language === 'es' ? 'No' : 'No'}</MenuItem>
                  </TextField>
                </Grid>
                
                {/* Número de dependientes (solo si tiene dependientes) */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="number"
                    name="dependentsCount"
                    label={language === 'es' ? 'Número de dependientes' : 'Number of dependents'}
                    value={formData.dependentsCount || 0}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    disabled={formData.hasDependents !== 'yes'}
                    required={formData.hasDependents === 'yes' && isNewUser}
                    InputProps={{
                      inputProps: { min: 0 },
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                {/* Botones de acción dentro del formulario */}
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleCancel}
                    sx={{ borderRadius: 2 }}
                  >
                    {language === 'es' ? 'Cancelar' : 'Cancel'}
                  </Button>
                  
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    {language === 'es' ? 'Guardar Cambios' : 'Save Changes'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          ) : (
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <List sx={{ p: 0 }}>
                  {/* Nombre */}
                  <ListItem sx={{ px: 1, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <PersonIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={language === 'es' ? 'Nombre' : 'Name'}
                      secondary={formData.displayName || '-'}
                    />
                  </ListItem>
                  
                  {/* Ocupación */}
                  <ListItem sx={{ px: 1, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <WorkIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={language === 'es' ? 'Ocupación' : 'Occupation'}
                      secondary={formData.occupation || '-'}
                    />
                  </ListItem>
                  
                  {/* Tipo de empleo */}
                  <ListItem sx={{ px: 1, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <BusinessIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={language === 'es' ? 'Tipo de empleo' : 'Employment Type'}
                      secondary={
                        formData.employmentType === 'employee' ? (language === 'es' ? 'Asalariado' : 'Employee') :
                        formData.employmentType === 'selfEmployed' ? (language === 'es' ? 'Independiente' : 'Self-employed') :
                        formData.employmentType === 'both' ? (language === 'es' ? 'Ambos' : 'Both') : '-'
                      }
                    />
                  </ListItem>
                  
                  {/* Estado civil */}
                  <ListItem sx={{ px: 1, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <WcIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={language === 'es' ? 'Estado civil' : 'Marital Status'}
                      secondary={
                        formData.maritalStatus === 'single' ? (language === 'es' ? 'Soltero/a' : 'Single') :
                        formData.maritalStatus === 'married' ? (language === 'es' ? 'Casado/a' : 'Married') :
                        formData.maritalStatus === 'divorced' ? (language === 'es' ? 'Divorciado/a' : 'Divorced') :
                        formData.maritalStatus === 'widowed' ? (language === 'es' ? 'Viudo/a' : 'Widowed') : '-'
                      }
                    />
                  </ListItem>
                  
                  {/* Dependientes */}
                  <ListItem sx={{ px: 1, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <FamilyRestroomIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={language === 'es' ? 'Dependientes' : 'Dependents'}
                      secondary={
                        formData.hasDependents === 'yes' ? 
                        (language === 'es' ? 
                          `Sí (${formData.dependentsCount})` : 
                          `Yes (${formData.dependentsCount})`) : 
                        (language === 'es' ? 'No' : 'No')
                      }
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          )}
        </Box>
        
        {/* Botones de acción - solo para modo vista */}
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
          {!isEditing && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleEdit}
              startIcon={<EditIcon />}
              sx={{ borderRadius: 2 }}
            >
              {language === 'es' ? 'Editar Perfil' : 'Edit Profile'}
            </Button>
          )}
        </Grid>
      </Paper>
      
      {/* Notificaciones */}
      <Snackbar open={success} autoHideDuration={3000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {language === 'es' ? 'Perfil actualizado exitosamente' : 'Profile updated successfully'}
        </Alert>
      </Snackbar>
      
      <Snackbar open={error !== null} autoHideDuration={3000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserProfile;
